import { useMutation } from "@tanstack/react-query"
import { type Questionnaire } from "fhir/r3"
import { Button, Checkboxes } from "nhsuk-react-components"
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useAppointmentQuery } from "../../api/fetchAppointment"
import { useHealthcareServiceQuery } from "../../api/fetchHealthcareService"
import { useQuestionnaireQuery } from "../../api/fetchQuestionnaire"
import { submitQuestionnaireResponse } from "../../api/submitQuestionnaireResponse"
import { ContactDetails } from "../../components/contactDetails/ContactDetails"
import { Loading } from "../../components/loading/Loading"
import { QuestionnaireError } from "../../components/question/QuestionnaireError"
import { ShowAnswer } from "../../components/question/ShowAnswer"
import { AuthenticationContext } from "../../context/AuthenticationContext"
import { BackButtonContext } from "../../context/BackButtonContext"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { useNhsAppJs } from "../../hooks/useNhsAppJs"
import { AppointmentCard } from "../appointment/appointmentViews/AppointmentCard"
import { QuestionnaireComplete } from "./QuestionnaireComplete"
import { useLocationQuery } from "../../api/fetchLocation"

const CancelScreenText = {
  confirmError: "You must confirm that you understand you are cancelling your appointment",
  intent:
    "You are about to submit a request to cancel the following appointment. Review your details and make sure everything is correct.",
  checkboxError: "Confirm that you are cancelling your appointment",
  checkboxLabel:
    "I understand that this cancellation request cannot be undone and confirm that I no longer need this appointment. ",
  submitButtonText: "Request cancellation",
  importantNotes: [
    "Cancelling this appointment may discharge you from care.",
    "Only cancel your appointment if you are sure you do not need this care.",
    "If you are not sure, contact your care provider.",
  ],
}

const RescheduleScreenText = {
  confirmError: "You must confirm that you understand that rescheduling cannot be undone",
  intent:
    "You are about to submit a request to reschedule your appointment. Review your details and make sure everything is correct.",
  checkboxError: "Confirm that you are rescheduling your appointment",
  checkboxLabel: "I understand that this request cannot be undone.",
  submitButtonText: "Submit request",
  importantNotes: undefined,
}

/**
 * Questionnaire Flow:
 *
 * `QuestionnaireStart.tsx` -> (`QuestionPage.tsx` -> `...` -> `QuestionPage.tsx`) -> `QuestionnaireSummary.tsx`
 *
 * This page:
 * - Uses `useParams` to pages action e.g. "cancel" or "reschedule"..
 * - Uses `QuestionnaireContext` to access questionnaire state and responses.
 * - Shows a summary of the questionnaire responses.
 * - Handles the submission of the questionnaire.
 *
 * @returns {JSX.Element} The rendered page.
 */
export const QuestionnaireSummary = () => {
  const params = useParams()

  const { setOnBackPress, setShowBackButton } = useContext(BackButtonContext)

  const { token } = useContext(AuthenticationContext)

  const { navigateToPage } = useNhsAppJs()

  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("QuestionnaireSummary must be used within a QuestionnaireProvider")
  }
  const { step, responses, questions } = questionnaireContext

  const [actionConfirmed, setActionConfirmed] = useState(false)
  const [actionError, setActionError] = useState(false)
  const screenText: typeof RescheduleScreenText | typeof CancelScreenText = useMemo(
    () => (params.patientAction === "reschedule" ? RescheduleScreenText : CancelScreenText),
    [params.patientAction],
  )

  const memoisedStep = useMemo(() => step, [step])

  const submitQuestionnaireMutation = useMutation({
    mutationFn: async () => {
      if (appointment && questionnaire) {
        await submitQuestionnaireResponse(params.odsId ?? "", appointment, questionnaire, responses, token)
      }
    },
    onSuccess: () => {
      if (setShowBackButton) {
        setShowBackButton(false)
      }
      navigateToPage({ relativePath: "../complete" })
    },
    onError: () => navigateToPage("error"),
  })

  useEffect(() => {
    if (!setOnBackPress) return
    if (!memoisedStep) {
      setOnBackPress("back")
      return
    }
    if (memoisedStep) {
      setOnBackPress({ relativePath: `../page/${memoisedStep - 1}` })
      return
    }
  }, [memoisedStep, setOnBackPress])

  useEffect(() => {
    document.title = "Review your request"
  }, [])

  const questionnaireQuery = useQuestionnaireQuery({
    odsId: params.odsId,
    questionnaireId: params.questionnaireId,
    token,
  })

  const appointmentQuery = useAppointmentQuery({
    appointmentId: params.appointmentId,
    token,
  })

  const healthcareServiceQuery = useHealthcareServiceQuery({
    odsId: params.odsId,
    token,
  })
  const appointment = appointmentQuery.data

  const locationId =
    appointment?.participantLocationReference?.length && appointment?.participantLocationReference?.length > 0
      ? appointment.participantLocationReference[0]?.reference?.split("Location/")[1]
      : undefined
  const { data: location } = useLocationQuery({ locationId, token })

  const { changeRequested } = useContext(AuthenticationContext)

  if (changeRequested) return <QuestionnaireComplete />

  if (appointmentQuery.isLoading || healthcareServiceQuery.isLoading || questionnaireQuery.isLoading) {
    return <Loading />
  }

  if (questionnaireQuery.isError || appointmentQuery.isError || healthcareServiceQuery.isError) {
    navigateToPage("error")
  }

  const clickActionConfirmed = () => {
    setActionError(false)
    setActionConfirmed(!actionConfirmed)
  }
  const clickSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    if (actionConfirmed) {
      if (!submitQuestionnaireMutation.isLoading) {
        submitQuestionnaireMutation.mutate()
      }
    } else {
      setActionError(true)
    }
  }

  const healthcareService = healthcareServiceQuery.data
  const questionnaire: Questionnaire | undefined = questionnaireQuery.data

  if (!healthcareService || !appointment || !questionnaire) {
    navigateToPage("error")
    return <></>
  }

  const hasAnyAnswers = responses.some((response) => {
    if (response.answer === null) return false
    const answers = response.answer ?? []
    if (answers.length < 1) return false

    return answers.some((value) => Object.keys(value).length > 0)
  })

  const confirmErrorString = actionError && screenText.checkboxError

  return (
    <form>
      {hasAnyAnswers ? (
        <>
          {actionError && <QuestionnaireError errorMessage={screenText.confirmError} formElementId="actionConfirmed" />}
          <h1 className="appointment-header">Review your request</h1>
          <p>{screenText.intent}</p>
          <AppointmentCard
            appointment={appointment}
            healthcareService={healthcareService}
            location={location}
            appointmentView
          />
          {questions.map((question) => {
            return <ShowAnswer question={question} topLevel key={question.linkId} />
          })}

          <h2>Confirm</h2>

          <div className="confirm-radio">
            <Checkboxes
              aria-invalid={actionError}
              error={confirmErrorString}
              id="actionConfirmed"
              name="actionConfirmed"
              onChange={clickActionConfirmed}
            >
              <Checkboxes.Box value="yes" checked={actionConfirmed}>
                {screenText.checkboxLabel}
              </Checkboxes.Box>
            </Checkboxes>
          </div>
          <Button disabled={submitQuestionnaireMutation.isLoading} type="submit" onClick={clickSubmit}>
            {screenText.submitButtonText}
          </Button>
        </>
      ) : (
        <ContactDetails header="Contact us" healthcareService={healthcareService} />
      )}
    </form>
  )
}
